import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAsignarFotos, useGaleria } from '@/hooks/useFotos';
import { useSubPartidas } from '@/hooks/useSubPartidas';
import { modoDev, obtenerUsuarioGuardado, usuarioDev } from '@/lib/auth';
import { type FiltroGaleria, cliente, esGestion } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type { FotoGaleria } from '@adelante/shared';
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  CircleNotch,
  ClipboardText,
  Images,
  MapPin,
  Trash,
  X,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Grilla de fotos (más recientes primero) agrupada por día, con visor a
 * pantalla completa y, opcionalmente, selección múltiple para asignar
 * sub-partida. La usan la galería general y la galería de cada obra.
 */

interface Props {
  filtro: FiltroGaleria;
  /** Galería general: muestra la obra en cada foto y en el visor. */
  mostrarObra?: boolean;
  /** Permite seleccionar fotos sueltas y asignarles sub-partida (vivienda). */
  seleccionable?: boolean;
  /** Ruta base de las galerías por obra (/campo/fotos o /admin/fotos). */
  base: string;
  hayFiltros?: boolean;
}

const iniciales = (n: string | null) =>
  (n ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

function etiquetaDia(iso: string): string {
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === hoy.toDateString()) return 'Hoy';
  if (d.toDateString() === ayer.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-CR', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Foto suelta = no viene de una ficha ni de una NC (se le puede cambiar la sub-partida). */
const esSuelta = (f: FotoGaleria) => !f.registro_id && !f.no_conformidad_id;

export function GaleriaFotos({
  filtro,
  mostrarObra = false,
  seleccionable = false,
  base,
  hayFiltros,
}: Props) {
  const galeria = useGaleria(filtro);
  const fotos = useMemo(() => galeria.data?.pages.flatMap((p) => p.fotos) ?? [], [galeria.data]);

  const porDia = useMemo(() => {
    const g: Array<{ dia: string; fotos: FotoGaleria[] }> = [];
    for (const f of fotos) {
      const dia = etiquetaDia(f.fecha);
      const ult = g[g.length - 1];
      if (ult && ult.dia === dia) ult.fotos.push(f);
      else g.push({ dia, fotos: [f] });
    }
    return g;
  }, [fotos]);

  const [visor, setVisor] = useState<number | null>(null);
  const [seleccion, setSeleccion] = useState<Set<string> | null>(null);
  const [asignarAbierto, setAsignarAbierto] = useState(false);

  function tocar(f: FotoGaleria, i: number) {
    if (!seleccion) return setVisor(i);
    if (!esSuelta(f)) return toast.info('Las fotos de fichas conservan la sub-partida de la ficha');
    const s = new Set(seleccion);
    if (s.has(f.id)) s.delete(f.id);
    else s.add(f.id);
    setSeleccion(s);
  }

  return (
    <div className="space-y-4">
      {seleccionable && fotos.length > 0 && (
        <div className="flex justify-end">
          {seleccion ? (
            <Button variant="outline" size="sm" onClick={() => setSeleccion(null)}>
              Cancelar selección
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setSeleccion(new Set())}>
              Seleccionar para asignar sub-partida
            </Button>
          )}
        </div>
      )}

      {galeria.isLoading && (
        <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" /> Cargando
          fotos…
        </p>
      )}
      {galeria.error && <p className="text-sm text-destructive">Error: {galeria.error.message}</p>}
      {!galeria.isLoading && !galeria.error && fotos.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
          <Images className="h-8 w-8" weight="bold" aria-hidden="true" />
          {hayFiltros ? 'No hay fotos con estos filtros.' : 'Aún no hay fotos.'}
        </Card>
      )}

      {porDia.map(({ dia, fotos: delDia }) => (
        <section key={dia} className="space-y-1.5">
          <h2 className="sticky top-0 z-10 bg-zinc-50/95 px-1 py-1 text-sm font-semibold backdrop-blur">
            {dia} <span className="font-normal text-muted-foreground">· {delDia.length}</span>
          </h2>
          <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8">
            {delDia.map((f) => {
              const i = fotos.indexOf(f);
              const sel = seleccion?.has(f.id);
              const pie = mostrarObra ? f.obra_codigo : (f.sub_partida_nombre ?? f.ficha_nombre);
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => tocar(f, i)}
                  className={cn(
                    'relative aspect-square overflow-hidden rounded bg-zinc-200',
                    sel && 'ring-4 ring-adelante-primary',
                    seleccion && !esSuelta(f) && 'opacity-50',
                  )}
                >
                  <img
                    src={f.thumb_url ?? f.url}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute left-1 top-1 rounded-full bg-white/90 px-1.5 text-[10px] font-semibold text-zinc-800">
                    {iniciales(f.autor_nombre)}
                  </span>
                  {f.latitud !== null && (
                    <span className="absolute right-1 top-1 rounded bg-adelante-primary px-1 text-[9px] font-bold text-black">
                      GPS
                    </span>
                  )}
                  {pie && (
                    <span className="absolute inset-x-0 bottom-0 flex items-center gap-0.5 truncate bg-black/55 px-1 py-0.5 text-left text-[10px] text-white">
                      {f.ficha_nombre && (
                        <ClipboardText className="h-3 w-3 shrink-0" weight="bold" />
                      )}
                      <span className={cn('truncate', mostrarObra && 'font-mono font-semibold')}>
                        {pie}
                      </span>
                    </span>
                  )}
                  {sel && (
                    <CheckCircle
                      className="absolute bottom-1 right-1 h-6 w-6 text-adelante-primary"
                      weight="fill"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {galeria.hasNextPage && (
        <Button
          variant="outline"
          className="w-full"
          disabled={galeria.isFetchingNextPage}
          onClick={() => galeria.fetchNextPage()}
        >
          {galeria.isFetchingNextPage ? 'Cargando…' : 'Cargar más fotos'}
        </Button>
      )}

      {seleccion && (
        <div className="fixed inset-x-0 bottom-16 z-30 border-t border-border bg-white px-4 py-2 md:bottom-0">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-2">
            <span className="text-sm">
              <strong>{seleccion.size}</strong> seleccionada{seleccion.size === 1 ? '' : 's'}
            </span>
            <Button disabled={seleccion.size === 0} onClick={() => setAsignarAbierto(true)}>
              Asignar sub-partida
            </Button>
          </div>
        </div>
      )}

      {seleccion && asignarAbierto && (
        <AsignarDialog
          ids={[...seleccion]}
          onCerrar={(ok) => {
            setAsignarAbierto(false);
            if (ok) setSeleccion(null);
          }}
        />
      )}

      {visor !== null && fotos[visor] && (
        <Visor
          fotos={fotos}
          indice={visor}
          onIndice={setVisor}
          onCerrar={() => setVisor(null)}
          hayMas={!!galeria.hasNextPage}
          onCargarMas={() => galeria.fetchNextPage()}
          mostrarObra={mostrarObra}
          base={base}
        />
      )}
    </div>
  );
}

// ============================================================================
// Asignar sub-partida
// ============================================================================

function AsignarDialog({ ids, onCerrar }: { ids: string[]; onCerrar: (ok: boolean) => void }) {
  const [sub, setSub] = useState<string>('');
  const asignar = useAsignarFotos();
  const subsQ = useSubPartidas();
  const subs = useMemo(
    () =>
      (subsQ.data?.data ?? [])
        .filter((s) => s.activo)
        .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true })),
    [subsQ.data],
  );

  async function confirmar() {
    try {
      const r = await asignar.mutateAsync({
        ids,
        sub_partida_id: sub === 'quitar' ? null : Number(sub),
      });
      toast.success(
        `${r.actualizadas} foto${r.actualizadas === 1 ? '' : 's'} actualizada${r.actualizadas === 1 ? '' : 's'}`,
      );
      onCerrar(true);
    } catch (e) {
      toast.error('No se pudo asignar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && !asignar.isPending && onCerrar(false)}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Asignar sub-partida</DialogTitle>
          <DialogDescription>
            {ids.length} foto{ids.length === 1 ? '' : 's'} seleccionada{ids.length === 1 ? '' : 's'}
            .
          </DialogDescription>
        </DialogHeader>
        <select
          className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
          value={sub}
          onChange={(e) => setSub(e.target.value)}
        >
          <option value="">Elegir sub-partida…</option>
          <option value="quitar">— Quitar sub-partida —</option>
          {subs.map((s) => (
            <option key={s.id} value={s.id}>
              {s.codigo} {s.nombre}
            </option>
          ))}
        </select>
        <DialogFooter>
          <Button variant="outline" onClick={() => onCerrar(false)} disabled={asignar.isPending}>
            Cancelar
          </Button>
          <Button onClick={confirmar} disabled={!sub || asignar.isPending}>
            {asignar.isPending ? 'Guardando…' : 'Asignar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Visor a pantalla completa
// ============================================================================

function Visor({
  fotos,
  indice,
  onIndice,
  onCerrar,
  hayMas,
  onCargarMas,
  mostrarObra,
  base,
}: {
  fotos: FotoGaleria[];
  indice: number;
  onIndice: (i: number) => void;
  onCerrar: () => void;
  hayMas: boolean;
  onCargarMas: () => void;
  mostrarObra: boolean;
  base: string;
}) {
  const f = fotos[indice] as FotoGaleria;
  const qc = useQueryClient();
  const [toqueX, setToqueX] = useState<number | null>(null);
  const yo = modoDev ? usuarioDev : obtenerUsuarioGuardado();
  const puedeBorrar = esSuelta(f) && (esGestion() || (yo && yo.id === f.autor_id));

  const ir = (d: number) => {
    const j = indice + d;
    if (j >= 0 && j < fotos.length) onIndice(j);
    else if (j >= fotos.length && hayMas) onCargarMas();
  };

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') ir(1);
      else if (e.key === 'ArrowLeft') ir(-1);
      else if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  });

  async function borrar() {
    if (!window.confirm('¿Quitar esta foto?')) return;
    try {
      await cliente.calidad.eliminarFoto(f.id);
      toast.success('Foto quitada');
      await qc.invalidateQueries({ queryKey: ['fotos'] });
      onCerrar();
    } catch (e) {
      toast.error('No se pudo quitar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black text-white">
      <div className="flex items-center justify-between px-3 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-full bg-white/15 p-2.5"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" weight="bold" />
        </button>
        <span className="text-xs text-white/70">
          {indice + 1} / {fotos.length}
          {hayMas ? '+' : ''}
        </span>
        {puedeBorrar ? (
          <button
            type="button"
            onClick={borrar}
            className="rounded-full bg-white/15 p-2.5"
            aria-label="Quitar foto"
          >
            <Trash className="h-5 w-5" weight="bold" />
          </button>
        ) : (
          <span className="w-10" />
        )}
      </div>

      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        onTouchStart={(e) => setToqueX(e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const x = e.changedTouches[0]?.clientX;
          if (toqueX !== null && x !== undefined && Math.abs(x - toqueX) > 50)
            ir(x < toqueX ? 1 : -1);
          setToqueX(null);
        }}
      >
        <img key={f.id} src={f.url} alt="" className="max-h-full max-w-full object-contain" />
        <button
          type="button"
          onClick={() => ir(-1)}
          disabled={indice === 0}
          className="absolute left-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/50 p-2 disabled:opacity-20 md:block"
          aria-label="Anterior"
        >
          <CaretLeft className="h-6 w-6" weight="bold" />
        </button>
        <button
          type="button"
          onClick={() => ir(1)}
          disabled={indice === fotos.length - 1 && !hayMas}
          className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-full bg-black/50 p-2 disabled:opacity-20 md:block"
          aria-label="Siguiente"
        >
          <CaretRight className="h-6 w-6" weight="bold" />
        </button>
      </div>

      <div className="space-y-0.5 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-2 text-sm">
        {mostrarObra && (
          <Link
            to={`${base}/${encodeURIComponent(f.obra_codigo)}`}
            onClick={onCerrar}
            className="font-mono font-semibold text-adelante-primary underline"
          >
            {f.obra_codigo}
            {f.obra_nombre ? ` · ${f.obra_nombre}` : ''}
          </Link>
        )}
        <p className="font-medium">
          {new Date(f.fecha).toLocaleString('es-CR', { dateStyle: 'medium', timeStyle: 'short' })} ·{' '}
          {f.autor_nombre ?? 'Sin usuario'}
        </p>
        {(f.sub_partida_codigo || f.ficha_nombre) && (
          <p className="text-white/80">
            {f.sub_partida_codigo && `${f.sub_partida_codigo} ${f.sub_partida_nombre}`}
            {f.ficha_nombre && ` · Ficha: ${f.ficha_nombre}`}
          </p>
        )}
        {f.nota && <p className="text-white/80">{f.nota}</p>}
        {f.latitud !== null && f.longitud !== null && (
          <a
            href={`https://www.google.com/maps?q=${f.latitud},${f.longitud}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-adelante-primary underline"
          >
            <MapPin className="h-4 w-4" weight="fill" /> Ver ubicación
          </a>
        )}
      </div>
    </div>
  );
}
